import { LenderStatusEnum, RoleEnum } from '@types';
import type { BrokerOrgStatus } from '@types';
import { get } from 'lodash';
import { create } from 'zustand';
import { devtools, persist, createJSONStorage } from 'zustand/middleware';

export type RuleTypeEnum = 'ADMIN' | 'STANDARD' | 'USER';

export type RulesType = RuleTypeEnum[]

export interface LenderType {
  hasBuyback?: boolean;
  hasAssetVerifications?: boolean;
  hasInspections?: boolean;
  organisationId: string;
  organisationName: string;
  roles?: RulesType;
}

export interface LenderBrokersType {
  hasBuyback?: boolean;
  hasAssetVerifications?: boolean;
  hasInspections?: boolean;
  lenderOrganisationId: string;
  lenderOrganisationName: string;
  status?: LenderStatusEnum | string;
  roles?: RulesType;
}

export interface organisationConfigType {
  hasBuyback?: boolean;
  hasAssetVerifications?: boolean;
  hasInspections?: boolean;
  assetVerificationConfig?: any,
  hasBrokers?: boolean,
  hasRequestAccess?: boolean,
  inspectionConfig?: {
    dailyQuota?: number;
    isEnabled?: boolean;
  },
  name?: string;
  organisationId?: string;
  primaryColour?: string;
  status?: string;
  webhookConfig?: any;
}

export interface BrokerMembershipType {
  brokerOrganisationId?: string;
  brokerOrganisationName?: string;
  organisationStatus?: string;
  roles?: string[];
  userStatus?: string;
}

export const deriveBrokerOrgState = (
  brokers?: BrokerMembershipType[] | null
): { brokerOrgStatus: BrokerOrgStatus; brokerOrgName: string } => {
  const memberships = (brokers ?? []).filter(
    (broker) => broker?.brokerOrganisationId
  );
  const activeOrg = memberships.find(
    (broker) => broker.organisationStatus === 'ACTIVE'
  );
  if (activeOrg) {
    return {
      brokerOrgStatus: 'ACTIVE',
      brokerOrgName: activeOrg.brokerOrganisationName ?? ''
    };
  }
  if (memberships.length > 0) {
    return {
      brokerOrgStatus: 'PENDING',
      brokerOrgName: memberships[0].brokerOrganisationName ?? ''
    };
  }
  return { brokerOrgStatus: 'NONE', brokerOrgName: '' };
};

const initialAuthState = () => ({
  token: '',
  user: {
    initials: '',
    avatarColour: 'DD3434'
  },
  lenders: [] as LenderType[],
  lenderBrokers: [] as LenderBrokersType[],
  brokers: [] as BrokerMembershipType[],
  selectedOrganisationId: '',
  organisationConfig: {
    hasBuyback: false,
    hasAssetVerifications: false,
    hasInspections: false,
    name: '',
    organisationId: '',
    primaryColour: '',
    status: ''
  } as organisationConfigType,
  brokerOrgStatus: 'NONE' as BrokerOrgStatus,
  brokerOrgName: ''
});

interface AuthStoreState {
  token: string;
  user?: any;
  brokers?: BrokerMembershipType[];
  lenders?: LenderType[];
  lenderBrokers?: LenderBrokersType[]
  selectedOrganisationId: string;
  organisationConfig: organisationConfigType;
  setorganisationConfig: (organisationConfig: organisationConfigType) => void;
  setLenders: (lenders: LenderType[]) => void;
  setLenderBrokers: (lenderBrokers: LenderBrokersType[]) => void;
  setBrokers: (brokers: BrokerMembershipType[]) => void;
  resetAuth: () => void;
  setToken: (token: string) => void;
  setUser: (user: any) => void;
  setSelectedOrganisationId: (organisationId: string) => void;
  isLender: () => boolean;
  isLenderBroker: () => boolean;
  isBrokerAdmin: () => boolean;
  isInspectionEnabled: () => boolean;
  canViewInspections: () => boolean;
  isAdmin: () => boolean;
  isNewUser: () => boolean;
  getLender: () => LenderType | null;
  canSelectLender: () => boolean;
  isBroker: () => boolean;
  brokerOrgStatus: BrokerOrgStatus;
  setBrokerOrgStatus: (status: BrokerOrgStatus) => void;
  brokerOrgName: string;
  setBrokerOrgName: (name: string) => void;
}

export const useAuthStore = create<AuthStoreState>()(
  devtools(
    persist(
      (set, getState) => ({
        ...initialAuthState(),
        resetAuth: () => set(initialAuthState()),
        setSelectedOrganisationId: (organisationId) => set({ selectedOrganisationId: organisationId }),
        setLenders: (lenders) => set({ lenders: lenders }),
        setLenderBrokers: (lenderBrokers) => set({ lenderBrokers: lenderBrokers }),
        setBrokers: (brokers) =>
          set({ brokers, ...deriveBrokerOrgState(brokers) }),
        setToken: (token) => set({ token }),
        setUser: (user) => set({ user }),
        setorganisationConfig: (organisationConfig) => set({ organisationConfig: organisationConfig }),
        isInspectionEnabled: () => {
          return getState().isLender() || getState().isLenderBroker();
        },
        canViewInspections: () => {
          return getState().isInspectionEnabled() || getState().isBrokerAdmin();
        },
        isLender: () => {
          return getState().lenders.some((lender) => lender.organisationId === getState().selectedOrganisationId);
        },
        getLender: () => {
          return getState().lenders.find((lender) => lender.organisationId === get(getState(), 'organisationConfig.organisationId')) || null;
        },
        isLenderBroker: () => {
          return getState().lenderBrokers.some((lenderBroker) => lenderBroker.status === LenderStatusEnum.Active);
        },
        isBrokerAdmin: () => {
          return getState().brokers.some((broker) => broker.roles?.includes(RoleEnum.ADMIN) && broker.organisationStatus === LenderStatusEnum.Active) || false;
        },
        isAdmin: () => {
          return getState().lenders.some((lender) => lender.roles?.includes(RoleEnum.ADMIN) && lender.organisationId === get(getState(), 'organisationConfig.organisationId')) || false;
        },
        isNewUser: () => {
          return !getState().isLender() && !getState().isLenderBroker() && !getState().isAdmin();
        },
        canSelectLender: () => {
          return getState().lenders.length > 1;
        },
        setBrokerOrgStatus: (status) => set({ brokerOrgStatus: status }),
        setBrokerOrgName: (name) => set({ brokerOrgName: name }),
        isBroker: () => {
          return getState().lenders.length === 0;
        },
      }),
      {
        name: 'auth',
        storage: createJSONStorage(() => localStorage)
      }
    )
  )
);
